import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Field, Input, Loading, Table } from "@/components/Ui";

const PIE = ["#102A43", "#147D78", "#D9A441", "#486581", "#9FB3C8", "#8C6D1F", "#2C7A7B", "#334E68"];
const iso = (d) => d.toISOString().slice(0, 10);

const money = (v) =>
  `AED ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Metric = ({ label, value, hint, testId }) => (
  <div className="rounded-[12px] border border-[#DFE6ED] bg-white p-4" data-testid={testId}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#667788]">{label}</p>
    <p className="mt-2 font-display text-2xl font-black tracking-tight text-[#102A43]">{value}</p>
    {hint && <p className="mt-1 text-xs text-[#8598ac]">{hint}</p>}
  </div>
);

export default function AdminAnalytics() {
  const today = new Date();
  const sixMonthsAgo = new Date(today.getTime() - 180 * 864e5);
  const [range, setRange] = useState({ start: iso(sixMonthsAgo), end: iso(today) });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/analytics", { params: range })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setData(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get("/admin/analytics/export",
        { params: { ...range, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adit-analytics-${range.start}-to-${range.end}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const t = data?.totals;

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">
              Reports & analytics
            </p>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tight text-[#102A43]">
              Platform performance
            </h2>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="From">
              <Input data-testid="analytics-start-input" type="date" value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })} />
            </Field>
            <Field label="To">
              <Input data-testid="analytics-end-input" type="date" value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })} />
            </Field>
            <Btn variant="ghost" className="px-4 py-2.5 text-xs" data-testid="export-analytics-xlsx"
              loading={busy === "xlsx"} onClick={() => download("xlsx")}>Export Excel</Btn>
            <Btn variant="ghost" className="px-4 py-2.5 text-xs" data-testid="export-analytics-csv"
              loading={busy === "csv"} onClick={() => download("csv")}>Export CSV</Btn>
          </div>
        </div>
      </Card>

      {data === null && <Loading label="Building the report…" />}
      {data === false && <p className="text-sm text-[#667788]">Could not load the report.</p>}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric testId="metric-rfqs" label="RFQs raised" value={t.rfqs}
              hint={`${t.avg_quotes_per_rfq} quotes per enquiry`} />
            <Metric testId="metric-quotes" label="Quotations submitted" value={t.quotes} />
            <Metric testId="metric-orders" label="Orders awarded" value={t.orders}
              hint={`${t.managed_orders} managed by ADIT`} />
            <Metric testId="metric-revenue" label="Order value" value={money(t.revenue)}
              hint={`${t.conversion_rate}% conversion`} />
          </div>

          {data.conversion && (
            <Card data-testid="analytics-conversion-card">
              <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">
                Acquisition funnel
              </h3>
              <p className="mt-1 text-sm text-[#667788]">
                Tracked events in the selected period.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(data.conversion.counts).map(([key, value]) => (
                  <Metric key={key} testId={`funnel-${key}`}
                    label={data.conversion.labels[key] || key} value={value} />
                ))}
                <Metric testId="funnel-rfq-completion" label="RFQ completion rate"
                  value={`${data.conversion.rfq_completion_rate}%`}
                  hint="Submitted vs RFQ forms opened" />
                <Metric testId="funnel-supplier-activation" label="Supplier activation"
                  value={`${data.conversion.supplier_activation_rate}%`}
                  hint="Quoted vs registered suppliers" />
              </div>
            </Card>
          )}

          <Card data-testid="analytics-trend-card">
            <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">
              Activity trend
            </h3>
            <div className="mt-4 h-[260px]" data-testid="analytics-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.trend} margin={{ left: -18, right: 8, top: 6 }}>
                  <defs>
                    <linearGradient id="gRfq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#102A43" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#102A43" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gOrd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#147D78" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#147D78" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="month" stroke="#8598ac" fontSize={11} tickLine={false} />
                  <YAxis stroke="#8598ac" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #DFE6ED", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="rfqs" name="RFQs" stroke="#102A43" strokeWidth={2}
                    fill="url(#gRfq)" />
                  <Area type="monotone" dataKey="quotes" name="Quotations" stroke="#D9A441"
                    strokeWidth={2} fillOpacity={0} />
                  <Area type="monotone" dataKey="orders" name="Orders" stroke="#147D78" strokeWidth={2}
                    fill="url(#gOrd)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card data-testid="analytics-revenue-card">
              <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">
                Order value by month
              </h3>
              <div className="mt-4 h-[240px]" data-testid="analytics-revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.trend} margin={{ left: -6, right: 8, top: 6 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="month" stroke="#8598ac" fontSize={11} tickLine={false} />
                    <YAxis stroke="#8598ac" fontSize={11} tickLine={false} axisLine={false}
                      tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <Tooltip formatter={(v) => money(v)}
                      contentStyle={{ borderRadius: 10, border: "1px solid #DFE6ED", fontSize: 12 }} />
                    <Bar dataKey="revenue" name="Order value" fill="#147D78" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card data-testid="analytics-category-card">
              <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">
                Top categories by enquiry
              </h3>
              {data.top_categories.length === 0 ? (
                <p className="mt-4 text-sm text-[#667788]">No enquiries in this period.</p>
              ) : (
                <div className="mt-4 h-[240px]" data-testid="analytics-category-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.top_categories} dataKey="rfqs" nameKey="category"
                        innerRadius={52} outerRadius={90} paddingAngle={2}>
                        {data.top_categories.map((c, i) => (
                          <Cell key={c.category} fill={PIE[i % PIE.length]} />
                        ))}
                      </Pie>
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #DFE6ED", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          <div>
            <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">
              Suppliers by awarded value
            </h3>
            <div className="mt-3">
              <Table testId="analytics-suppliers-table" head={["Supplier", "Orders", "Order value"]}>
                {data.top_suppliers.map((s) => (
                  <tr key={s.supplier} data-testid={`analytics-supplier-row-${s.supplier}`}>
                    <td className="font-semibold text-[#102A43]">{s.supplier}</td>
                    <td>{s.orders}</td>
                    <td>{money(s.revenue)}</td>
                  </tr>
                ))}
                {data.top_suppliers.length === 0 && (
                  <tr><td colSpan={3} className="text-sm text-[#667788]">No orders in this period.</td></tr>
                )}
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
